import { fromJS } from 'immutable';
import { get, mapValues, pickBy, identity } from 'lodash';
import moment from 'moment';

import {
  AchievementRecord,
  AgreementRecord,
  ApiUserRecord,
  AreaRecord,
  AvailableDateRecord,
  AvailableDateSettingRecord,
  BasicSchemaItemRecord,
  BasicSchemaRecord,
  BookingInvoiceItemAttestedDateRecord,
  BookingInvoiceItemBookedDateRecord,
  BookingInvoiceItemRecord,
  BookingPaymentItemAttestedDateRecord,
  BookingPaymentItemBookedDateRecord,
  BookingPaymentItemRecord,
  BookingRecord,
  BookingResourceRecord,
  BudgetRecord,
  CompetencyRecord,
  ContactRecord,
  CustomerAgreementInvoiceSettingRecord,
  CustomerAttestRecord,
  CustomerAttestResourceRecord,
  CustomerBjornLundenSettingRecord,
  CustomerFortnoxSettingRecord,
  CustomerListRecord,
  CustomerRecord,
  DayRecord,
  DocumentTemplateRecord,
  DomainRecord,
  EmailRecord,
  EmploymentContractPreviewRecord,
  EmploymentContractRecord,
  EmploymentNumberRecord,
  EntityViewRecord,
  EventRecord,
  ExpenseRecord,
  FileRecord,
  HolidayRecord,
  InquiryRecord,
  InvoiceAddressRecord,
  InvoiceDeliveryTypeRecord,
  InvoiceDetailRecord,
  InvoiceRecord,
  ItemsRecord,
  JobBoardRecord,
  MileageAllowanceRecord,
  MunicipalityRecord,
  OccupationNameRecord,
  OneflowCollectionRecord,
  OneflowTemplateRecord,
  PaymentCollectionRecord,
  PaymentRecord,
  PaymentRowRecord,
  PersonRecord,
  PreviewBookingSmsRecord,
  PricePeriodRecord,
  QualifyingDeductionRecord,
  QuestionnaireRecord,
  RecruitmentAdApplicationRecord,
  RecruitmentAdContactRecord,
  RecruitmentAdRecord,
  RecruitmentStatusRecord,
  ReplacementModelPeriodDeviationRecord,
  ReplacementModelPeriodRecord,
  ReplacementModelRecord,
  ResourceFortnoxSettingRecord,
  ResourceListRecord,
  ResourceRecord,
  ServiceRecord,
  SignableDocumentRecord,
  PushNotificationRecord,
  SmsRecord,
  StaffingCompanyGroupRecord,
  StaffingCompanyGroupUsageRecord,
  StaffingCompanyRecord,
  StatisticRecord,
  TaskRecord,
  TermRecord,
  TimeplanUnitRecord,
  UserRecord,
  WeeklyBookingRecord,
  WeeklyTimeReportSummaryRecord,
  WorkTypeRecord,
} from '../entities/records';

/**
 *
 * @param RecordType record type to convert to before applying normalization transforms
 * @param normalizer any additional transforms for the newly casted record type, for instance to
 * convert deeply nested structures for cases when you don't want to normalize sub structures
 */
const recordTypeNormalizer =
  (RecordType, normalizer = identity) =>
    entity =>
      normalizer(new RecordType(fromJS(entity)));

// Slim responses (v2 agreement, dropdown lookup) omit `periods` per pricelist.
// When the raw input doesn't have a periods key, set the record's periods to
// undefined so the entity-store merge (mergeWith ignoreUndefinedMerger) keeps
// whatever periods are already there. Otherwise a slim response that lands
// after a heavy one would clobber valid periods with an empty List.
const replacementModelNormalizer = (entity) => {
  const hasPeriods = entity != null && Object.prototype.hasOwnProperty.call(entity, 'periods');
  const record = new ReplacementModelRecord(fromJS(entity));
  if (!hasPeriods) {
    return record.set('periods', undefined);
  }
  return record.set('periods', record.get('periods').map(period =>
    new ReplacementModelPeriodRecord(period)
      .set('workType', new WorkTypeRecord(period.get('workType')).id)
      .set('deviations', period
        .get('deviations')
        .map(deviation => new ReplacementModelPeriodDeviationRecord(deviation)))
      .set('overtime1deviations', period
        .get('overtime1deviations') ? period
          .get('overtime1deviations')
          .map(deviation => new ReplacementModelPeriodDeviationRecord(deviation)) :
        null,
      )
      .set('overtime2deviations', period
        .get('overtime2deviations') ? period
          .get('overtime2deviations')
          .map(deviation => new ReplacementModelPeriodDeviationRecord(deviation)) :
        null,
      ),
  ));
};

const resourceNormalizer =
  recordTypeNormalizer(
    ResourceRecord,
    resource => (resource.get('places') ? resource.set(
      'places',
      resource.get('places').map(place => new AreaRecord(place)),
    ) : resource)
      .set('gdprAcceptedAt', resource.get('gdprAcceptedAt') ?
        moment(resource.get('gdprAcceptedAt')).format(moment.HTML5_FMT.DATE)
        : resource.get('gdprAcceptedAt')),
  );

/**
 *
 * @param entityKey
 * @param recordNormalizer converts values to record. Either a plain record constructor that returns
 * a record or a normalizing function that returns a record
 */
const toEntityRecord = (entityKey, recordNormalizer) => (data) => {
  const entities = get(data, ['entities', entityKey]);
  return !entities ? null : mapValues(entities, entity => recordNormalizer(entity));
};

const toNewEntityRecord = (entityKey, recordType) =>
  toEntityRecord(entityKey, recordTypeNormalizer(recordType));

const toResourceRecords = toEntityRecord('resources', resourceNormalizer);
const toReplacementModelRecords = toEntityRecord(
  'replacementModels',
  replacementModelNormalizer,
);

/**
 * Returns appropriate keys converted to equivalent record types, other data passes through
 */
export default function toRecords(data) {
  if (!data.entities) {
    return data;
  }

  const entities = pickBy({
    ...data.entities,
    achievements: toNewEntityRecord('achievements', AchievementRecord)(data),
    agreements: toNewEntityRecord('agreements', AgreementRecord)(data),
    apiUsers: toNewEntityRecord('apiUsers', ApiUserRecord)(data),
    areas: toNewEntityRecord('areas', AreaRecord)(data),
    availableDates: toNewEntityRecord('availableDates', AvailableDateRecord)(data),
    availableDateSettings: toNewEntityRecord('availableDateSettings', AvailableDateSettingRecord)(data),
    basicSchemaItems: toNewEntityRecord('basicSchemaItems', BasicSchemaItemRecord)(data),
    basicSchemas: toNewEntityRecord('basicSchemas', BasicSchemaRecord)(data),
    bookingInvoiceItemAttestedDates: toNewEntityRecord('bookingInvoiceItemAttestedDates', BookingInvoiceItemAttestedDateRecord)(data),
    bookingInvoiceItemBookedDates: toNewEntityRecord('bookingInvoiceItemBookedDates', BookingInvoiceItemBookedDateRecord)(data),
    bookingInvoiceItems: toNewEntityRecord('bookingInvoiceItems', BookingInvoiceItemRecord)(data),
    bookingPaymentItemAttestedDates: toNewEntityRecord('bookingPaymentItemAttestedDates', BookingPaymentItemAttestedDateRecord)(data),
    bookingPaymentItemBookedDates: toNewEntityRecord('bookingPaymentItemBookedDates', BookingPaymentItemBookedDateRecord)(data),
    bookingPaymentItems: toNewEntityRecord('bookingPaymentItems', BookingPaymentItemRecord)(data),
    bookingResources: toNewEntityRecord('bookingResources', BookingResourceRecord)(data),
    bookings: toNewEntityRecord('bookings', BookingRecord)(data),
    budgets: toNewEntityRecord('budgets', BudgetRecord)(data),
    competencies: toNewEntityRecord('competencies', CompetencyRecord)(data),
    contacts: toNewEntityRecord('contacts', ContactRecord)(data),
    customerAgreementInvoiceSettings: toNewEntityRecord('customerAgreementInvoiceSettings', CustomerAgreementInvoiceSettingRecord)(data),
    customerAttestResources: toNewEntityRecord('customerAttestResources', CustomerAttestResourceRecord)(data),
    customerAttests: toNewEntityRecord('customerAttests', CustomerAttestRecord)(data),
    customerBjornLundenSettings: toNewEntityRecord('customerBjornLundenSettings', CustomerBjornLundenSettingRecord)(data),
    customerFortnoxSettings: toNewEntityRecord('customerFortnoxSettings', CustomerFortnoxSettingRecord)(data),
    customerLists: toNewEntityRecord('customerLists', CustomerListRecord)(data),
    customers: toNewEntityRecord('customers', CustomerRecord)(data),
    days: toNewEntityRecord('days', DayRecord)(data),
    documentTemplates: toNewEntityRecord('documentTemplates', DocumentTemplateRecord)(data),
    domains: toNewEntityRecord('domains', DomainRecord)(data),
    emails: toNewEntityRecord('emails', EmailRecord)(data),
    employmentContractPreviews: toNewEntityRecord('employmentContractPreviews', EmploymentContractPreviewRecord)(data),
    employmentContracts: toNewEntityRecord('employmentContracts', EmploymentContractRecord)(data),
    employmentNumbers: toNewEntityRecord('employmentNumbers', EmploymentNumberRecord)(data),
    entityViews: toNewEntityRecord('entityViews', EntityViewRecord)(data),
    events: toNewEntityRecord('events', EventRecord)(data),
    expenses: toNewEntityRecord('expenses', ExpenseRecord)(data),
    files: toNewEntityRecord('files', FileRecord)(data),
    geoCodes: toNewEntityRecord('geoCodes', AreaRecord)(data),
    holidays: toNewEntityRecord('holidays', HolidayRecord)(data),
    inquiries: toNewEntityRecord('inquiries', InquiryRecord)(data),
    invoiceAddresses: toNewEntityRecord('invoiceAddresses', InvoiceAddressRecord)(data),
    invoiceDeliveryTypes: toNewEntityRecord('invoiceDeliveryTypes', InvoiceDeliveryTypeRecord)(data),
    invoiceDetails: toNewEntityRecord('invoiceDetails', InvoiceDetailRecord)(data),
    invoices: toNewEntityRecord('invoices', InvoiceRecord)(data),
    items: toNewEntityRecord('items', ItemsRecord)(data),
    jobBoards: toNewEntityRecord('jobBoards', JobBoardRecord)(data),
    mileageAllowances: toNewEntityRecord('mileageAllowances', MileageAllowanceRecord)(data),
    municipalities: toNewEntityRecord('municipalities', MunicipalityRecord)(data),
    occupationNames: toNewEntityRecord('occupationNames', OccupationNameRecord)(data),
    oneflowCollections: toNewEntityRecord('oneflowCollections', OneflowCollectionRecord)(data),
    oneflowTemplates: toNewEntityRecord('oneflowTemplates', OneflowTemplateRecord)(data),
    paymentCollections: toNewEntityRecord('paymentCollections', PaymentCollectionRecord)(data),
    paymentRows: toNewEntityRecord('paymentRows', PaymentRowRecord)(data),
    payments: toNewEntityRecord('payments', PaymentRecord)(data),
    people: toNewEntityRecord('people', PersonRecord)(data),
    previewBookingSmses: toNewEntityRecord('previewBookingSmses', PreviewBookingSmsRecord)(data),
    pricePeriods: toNewEntityRecord('pricePeriods', PricePeriodRecord)(data),
    qualifyingDeductions: toNewEntityRecord('qualifyingDeductions', QualifyingDeductionRecord)(data),
    questionnaire: toNewEntityRecord('questionnaire', QuestionnaireRecord)(data),
    recruitmentAdApplications: toNewEntityRecord('recruitmentAdApplications', RecruitmentAdApplicationRecord)(data),
    recruitmentAdContacts: toNewEntityRecord('recruitmentAdContacts', RecruitmentAdContactRecord)(data),
    recruitmentAds: toNewEntityRecord('recruitmentAds', RecruitmentAdRecord)(data),
    recruitmentStatuses: toNewEntityRecord('recruitmentStatuses', RecruitmentStatusRecord)(data),
    replacementModels: toReplacementModelRecords(data),
    resourceFortnoxSettings: toNewEntityRecord('resourceFortnoxSettings', ResourceFortnoxSettingRecord)(data),
    resourceLists: toNewEntityRecord('resourceLists', ResourceListRecord)(data),
    resources: toResourceRecords(data),
    services: toNewEntityRecord('services', ServiceRecord)(data),
    signableDocuments: toNewEntityRecord('signableDocuments', SignableDocumentRecord)(data),
    pushNotifications: toNewEntityRecord('pushNotifications', PushNotificationRecord)(data),
    smses: toNewEntityRecord('smses', SmsRecord)(data),
    staffingCompanies: toNewEntityRecord('staffingCompanies', StaffingCompanyRecord)(data),
    staffingCompanyGroups: toNewEntityRecord('staffingCompanyGroups', StaffingCompanyGroupRecord)(data),
    staffingCompanyGroupUsages: toNewEntityRecord('staffingCompanyGroupUsages', StaffingCompanyGroupUsageRecord)(data),
    statistics: toNewEntityRecord('statistics', StatisticRecord)(data),
    tasks: toNewEntityRecord('tasks', TaskRecord)(data),
    terms: toNewEntityRecord('terms', TermRecord)(data),
    timeplanUnits: toNewEntityRecord('timeplanUnits', TimeplanUnitRecord)(data),
    users: toNewEntityRecord('users', UserRecord)(data),
    weeklyBookings: toNewEntityRecord('weeklyBookings', WeeklyBookingRecord)(data),
    weeklyTimeReportSummaries: toNewEntityRecord('weeklyTimeReportSummaries', WeeklyTimeReportSummaryRecord)(data),
    workTypes: toNewEntityRecord('workTypes', WorkTypeRecord)(data),
  });

  return {
    ...data,
    entities,
  };
}
